import { Inject, Injectable } from '@angular/core';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { SessionService } from '../../../../shared/services/session.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { CsvExporterService } from '../../../../shared/modules/csv-exporter';
import { ApiService } from '../../../../shared/services/api.service';

const ADD_PROFILE_KEY = 'pools-add-profiles';
const ITEMS_PER_REQUEST = 1;

@Injectable({
    providedIn: 'root'
})
export class AddProfileService {

    private readonly addProfileStore: BehaviorSubject<AppProfileStore> = new BehaviorSubject<AppProfileStore>(null);
    private readonly key$ = this.sessionService.session$.pipe(map(i => this.getKey(i.orgId)));

    private getKey(orgId) {
        return `${ADD_PROFILE_KEY}.${orgId}`;
    }

    constructor(
        @Inject(LOCAL_STORAGE) private readonly storageService: StorageService,
        private readonly sessionService: SessionService,
        private readonly csvService: CsvExporterService,
        private readonly apiService: ApiService
    ) { }

    getPoolStore(poolId): Observable<{
        pool: PoolProcess,
        bulkProfiles: BulkProfile[]
    }> {
        if (this.addProfileStore.value === null) {
            //init pool store from storage
            return this.key$.pipe(
                tap(key => this.addProfileStore.next(this.storageService.get(key) ?? { pools: {}, bulkProfiles: [] })),
                switchMap(() => this.getPoolStore(poolId))
            )
        }

        return this.addProfileStore.pipe(map(store => {
            return {
                pool: store.pools[poolId] ?? { state: 'form' },
                bulkProfiles: [...store.bulkProfiles.filter(i => i.poolId === poolId)]
            };
        }))
    }

    updatePoolState({ poolId, state }: { poolId: string, state: 'form' | 'upload' }) {
        let storage = this.addProfileStore.value;
        let pool = storage.pools[poolId] ? { ...storage.pools[poolId], state } : { state };
        storage.pools[poolId] = pool;

        this.updateStore(storage);
    }

    addProfiles({ bulkProfiles }: { bulkProfiles: BulkProfile[] }) {
        let storage = this.addProfileStore.value;
        this.updateStore({ ...storage, bulkProfiles: [...storage.bulkProfiles, ...bulkProfiles] });
    }

    resetProfile(poolId: string) {
        let storage = this.addProfileStore.value;
        let pools = storage.pools;
       pools[poolId] = { state: 'upload' };

        this.updateStore({ pools, bulkProfiles: [...storage.bulkProfiles.filter(p => p.poolId !== poolId)] });
    }

    exportFailures(poolId: string) {
        let storage = this.addProfileStore.value;
        let failures = storage.bulkProfiles.filter(profile => profile.status === 'failed' && profile.poolId === poolId);
        let headers = ['activationCode', 'iccid', 'confirmationCode', "errorMessage"];
        this.csvService.generate(
            failures.map(({ activationCode, iccid, confirmationCode, errorMessages }) => ({
                activationCode,
                iccid: iccid ?? '',
                confirmationCode: confirmationCode ?? '',
                errorMessage: errorMessages.join()
            })), "AddProfileFailureReport", { headers: headers }
        );
    }

    execute(poolId: string) {
        let store = this.addProfileStore.value;
        let { bulkProfiles } = store;
        let readyItems = bulkProfiles.filter(i => i.status === 'ready' && i.poolId === poolId);
        if (readyItems.length === 0) return;
        let requestItems = readyItems.slice(0, ITEMS_PER_REQUEST);
        for (let item of requestItems) {
            //update state and executionDate,
            item.executionDate = new Date();
            item.status = 'executing';
        }
        let requests = requestItems.reduce((request, item) => {
            request[item.uuid] = {
                command: "app_rspmbridge.create_subscription",
                params: {
                    poolId: poolId,
                    iccid: item.iccid,
                    activationCode: item.activationCode,
                    confirmationCode: item.confirmationCode
                }
            }
            return { ...request };
        }, {})

        store.pools[poolId] = { startExecutionTime: new Date(), ...store.pools[poolId], endExecutionTime: undefined };

        this.updateStore(store);
        this.apiService.executeMultipleCommandRequest(requests).subscribe(res => {
            let store = this.addProfileStore.value;
            let { bulkProfiles } = store;
            for (let uuid in res) {
                bulkProfiles = bulkProfiles.map(i => {
                    if (i.uuid != uuid) return { ...i };
                    if (!res[uuid].success)
                        return {
                            ...i,
                            status: 'failed',
                            errorMessages: res[uuid].errorMessages
                        };
                    return {
                        ...i,
                        status: 'done',
                        params: res[uuid].params,
                        errorMessages: undefined
                    }
                });
            }
            let hasOtherMemberToProcess = bulkProfiles.some(i => i.status === 'ready' && i.poolId === poolId)
            if (!hasOtherMemberToProcess) {
                store.pools[poolId] = { ...store.pools[poolId], endExecutionTime: new Date() }
            }

            this.updateStore({ ...store, bulkProfiles });
            if (hasOtherMemberToProcess) this.execute(poolId);
        });
    }

    resetByStatus({ poolId, status }: { poolId: string, status: BulkProfileStatus }) {
      this.setByStatus({poolId, status, newStatus: 'ready'})
    }

    pause({ poolId }: { poolId: string }){
        this.setByStatus({ poolId, status: 'ready', newStatus: 'paused'})
    }

    private setByStatus({ poolId, status, newStatus }: { poolId: string, status: BulkProfileStatus, newStatus: BulkProfileStatus }) {
        let storage = this.addProfileStore.value;
        storage = { ...storage, bulkProfiles: storage.bulkProfiles.map(profile => (profile.poolId === poolId && profile.status === status) ? { ...profile, status: newStatus } : { ...profile }) }
        this.updateStore(storage);
    }

    private initProfilesPoolStore(orgId) {
        let response = this.storageService.get(this.getKey(orgId)) ?? { pools: {}, bulkProfiles: [] };
        this.addProfileStore.next(response);
        return response;
    }

    startInProgessCampaign(orgId) {
        let store = this.addProfileStore.value;
        if (!store) store = this.initProfilesPoolStore(orgId);
        for (let poolId in store.pools) {
            if (store.pools[poolId].startExecutionTime && !store.pools[poolId].endExecutionTime) {
                console.log("restarting Rspm Pool Add Profile Campaign", poolId);
                this.checkPendingItemsState({ poolId }).subscribe(() => this.execute(poolId))
            }
        }
    }

    checkPendingItemsState({ poolId }: { poolId: string}) {

        let store = this.addProfileStore.value;
        let { bulkProfiles } = store;
        let executingItems = bulkProfiles.filter(i => i.status === 'executing' && i.poolId === poolId);

        let requests = executingItems.reduce((request, item) => {
            request[item.uuid] = {
                command: "app_rspmbridge.find_subscription",
                params: {
                    poolId: poolId,
                    activationCode: item.activationCode
                }
            }
            return { ...request };
        }, {});

        return this.apiService.executeMultipleCommandRequest(requests).pipe(tap(
            res => {
                let store = this.addProfileStore.value;
                let { bulkProfiles } = store;
                let startExecutionTime = new Date(store.pools[poolId].startExecutionTime);

                for (let uuid in res) {
                    bulkProfiles = bulkProfiles.map(i => {

                        // backend is not sending creationTime in the right format.
                        // adding a check to add the Z only if missing, that will guarantee compantibility once fixed
                        // after backend code will be fixed we can remove the check and use data as come from backend

                        if (i.uuid != uuid) return { ...i };
                        if (
                            res[uuid].success &&
                            res[uuid].params.subscriptions.some(s =>
                                s.status === 'available' &&
                                (s.creationDate?.endsWith('Z') || s.creationDate?.endsWith('z') ? new Date(s.creationDate) : new Date(s.creationDate+"Z"))> startExecutionTime
                                && s.iccid === i.iccid
                            )
                        ) {
                            return {
                                ...i,
                                status: 'done'
                            };
                        }

                        return {
                            ...i,
                            status: 'ready'
                        }
                    });
                }
                this.updateStore({ ...store, bulkProfiles });
            }
        ))
    }

    private updateStore(store: AppProfileStore) {
        let orgId = this.sessionService.currentSession.orgId;
        let key = this.getKey(orgId);
        this.storageService.set(key, store);
        this.addProfileStore.next(store);
    }
}

type AppProfileStore = {
    bulkProfiles: BulkProfile[],
    pools: {
        [key: string]: PoolProcess
    }
}

type PoolProcess = {
    startExecutionTime?: Date,
    endExecutionTime?: Date,
    state: "form" | "upload",
}

export type BulkProfile = {
    poolId: string,
    uuid: string,
    iccid: string,
    activationCode: string,
    confirmationCode: string,
    status: BulkProfileStatus,
    executionDate?: Date,
    errorMessages?: string[],
    params?: unknown
}

export type BulkProfileStatus = "ready" | "executing" | "done" | "failed" | "paused";


